import { DocumentNode, gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import PluginEditor from '@draft-js-plugins/editor';
import {
  EditorState,
  RawDraftContentState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { logError } from '@/imports/logging/ClientLogger';
import { cleanEditorStateMentions } from '@/imports/ui/components/TextEditor/utils';
import { content_edited } from '@/analytics';
import { minutesSince } from '@/analytics/contentAnalysis';

import { EditComment_CommentFragment } from './graphql-types/EditComment_CommentFragment';
import {
  EditComment_updateComment,
  EditComment_updateCommentVariables,
} from './graphql-types/EditComment_updateComment';

import { CommentForm } from '../CommentForm';

const COMMENT_FRAGMENT = gql`
  fragment EditComment_CommentFragment on Comment {
    _id
    dateCreated
    bodyContentState {
      ...RawDraftContentStateFragment
    }
    versionFiles {
      _id
      file {
        _id
        name
        isVideo
        isImage
        url
      }
    }
    embeddedImages {
      imageId
      imageProxy(input: { fit: "crop", ar: "4:3", h: 384 })
    }
  }
  ${RawDraftContentStateFragmentAST}
`;

const UPDATE_COMMENT = gql`
  mutation EditComment_updateComment($input: UpdateCommentInput!) {
    updateComment(input: $input) {
      data {
        _id
        ...EditComment_CommentFragment
      }
    }
  }
  ${COMMENT_FRAGMENT}
`;

type FormValues = {
  body: EditorState;
  images: ({ imageId: string } | { videoId: string })[];
  attachments: { versionFileId: string; fileName: string }[];
};

export const EditComment: React.FC<{
  comment: EditComment_CommentFragment;
  onClose: () => void;
}> & {
  commentFragment: DocumentNode;
} = ({ comment, onClose }) => {
  const [updateComment, { loading }] = useMutation<
    EditComment_updateComment,
    EditComment_updateCommentVariables
  >(UPDATE_COMMENT);
  const toast = useToast();
  const methods = useForm<FormValues>({
    defaultValues: {
      body: (() => {
        const initialContent = convertFromRaw(
          comment.bodyContentState as unknown as RawDraftContentState
        );
        const cleanedEditorState = cleanEditorStateMentions(
          EditorState.createWithContent(initialContent)
        );
        return EditorState.moveSelectionToEnd(cleanedEditorState);
      })(),
      attachments: comment?.versionFiles?.map((files) => {
        return {
          versionFileId: files?._id,
          fileName: files?.file?.name || 'cant-load.file',
        };
      }),
      images: comment.embeddedImages,
    },
  });

  const bodyRef = React.useRef<PluginEditor>(null);
  const editor = bodyRef.current;
  React.useEffect(() => {
    if (editor) {
      editor.focus();
    }
  }, [editor]);

  const updateCommentHandler = methods.handleSubmit(async (params) => {
    try {
      await updateComment({
        variables: {
          input: {
            id: comment._id,
            data: {
              body: convertToRaw(params.body.getCurrentContent()),
              versionFileIds: params.attachments.map(
                (attachment) => attachment.versionFileId
              ),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
            },
          },
        },
      });

      // Track content edit
      content_edited({
        entity: {
          id: comment._id,
          type: 'comment',
        },
        content: {
          contentType: 'comment',
        },
        timing: {
          timeSinceCreation: (comment as any).dateCreated ? minutesSince((comment as any).dateCreated) : 0,
        },
      });

      methods.reset();
      onClose();
    } catch (e) {
      toast({
        status: 'error',
        description: 'Failed to edit comment',
      });
      logError(e);
    }
  });

  return (
    <FormProvider {...methods}>
      <CommentForm
        onSubmit={updateCommentHandler}
        ref={bodyRef}
        submitButtonText="Save changes"
        onClickCancel={() => {
          methods.reset();
          onClose();
        }}
        isSubmitting={loading}
      />
    </FormProvider>
  );
};

EditComment.commentFragment = COMMENT_FRAGMENT;
